import { useCallback, useRef, useState } from "react";

/**
 * Like useState but the returned setter debounces the value update.
 * The setter still updates immediately internally; only the *returned* value
 * is delayed by `delay` ms after the last call.
 */
export function useDebouncedState<T>(
  initialValue: T,
  delay: number
): [T, (value: T) => void] {
  const [debouncedValue, setDebouncedValue] = useState<T>(initialValue);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setValue = useCallback(
    (value: T) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
    },
    [delay]
  );

  return [debouncedValue, setValue];
}
